import {FormikHelpers} from "formik"
import {useDispatch, useSelector} from "react-redux"
import {useNavigate} from "react-router-dom"
import {TLogin, TRegister} from "../types"
import {userLogin, userRegister, userPassRequest, userPassReset, validateRegisterForm} from "../api"
import redirect from '../utils/redirect'
import {useAppContext} from "../AppContext"

const useFormActions = () => {
  const dispatch = useDispatch()
  const {appType} = useAppContext()
  const lang = useSelector(({reducer}: any) => reducer.lang)
  const navigate = useNavigate()

  const submitLogin = (values: any, actions?: FormikHelpers<any>) => {
    const loginObj: TLogin = {
      login: values.login,
      password: values.password,
      gRecaptchaResponse: values.captcha || ''
    }

    return userLogin(loginObj)
      .then((res) => {
        if ( res['stack-trace'] ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'fail',
              text: 'Failed to login',
            }
          })

          return
        }

        handleResult(res, actions)

        if ( res.authorized ) {
          if ( appType === 'pro' && res['two-factor'] ) {
            switch ( res['two-factor'] ) {
              case 'telegram':
                navigate('/site/ta')
                break
              case 'google':
                navigate('/site/ga')
                break
              case 'mail':
                navigate('/site/ma')
                break
            }
          } else { redirect(appType, true) }
        }
      })
      .catch(err => {
        dispatch({
          type: 'SET_SHOW_POPUP',
          payload: {
            isOpen: true,
            type: 'fail',
            text: err,
          }
        })
      })
  }

  const submitRegister = (values: any, actions?: FormikHelpers<any>) => {
    const regObj: TRegister = {
      login: values.login,
      email: values.email,
      password: values.password,
      gRecaptchaResponse: values.captcha || ''
    }

    return userRegister(regObj)
      .then((res) => {
        if ( res['stack-trace'] ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'fail',
              text: 'Failed to register',
            }
          })

          return
        }

        handleResult(res, actions)

        if ( res.registered ) {
          window.location.href = (process.env.NODE_ENV === 'development')
            ? 'https://loc.surfe.be/site/react-login?token=' + res.token
            : '/site/react-login?token=' + res.token
        }
      })
  }

  const submitRequest = (values: any) => {
    return userPassRequest({email: values.email})
      .then((res) => {
        if ( res['stack-trace'] ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'fail',
              text: 'Error accrued while sending email. Try again later.',
            }
          })

          return
        }

        if ( res.error ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'time',
              text: res.error,
            }
          })
        }

        if ( res.status ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'success',
              text: 'An email has been sent with instructions for resetting your password',
            }
          })

          navigate('/login')
        }
      })
      .catch(() => {
        dispatch({
          type: 'SET_SHOW_POPUP',
          payload: {
            isOpen: true,
            type: 'fail',
            text: 'Error accrued while sending email. Try again later.',
          }
        })
      })
  }

  const submitReset = (values: any, uid: string = '', key: string = '') => {
    const resetObj = {
      uid,
      key,
      password: values.password
    }

    return userPassReset(resetObj)
      .then((res) => {
        if ( res['stack-trace'] ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'fail',
              text: 'Error accrued while changing password. Try again later.',
            }
          })

          return
        }

        if ( res.error ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'time',
              text: res.error,
            }
          })
        }

        if ( res.status ) {
          dispatch({
            type: 'SET_SHOW_POPUP',
            payload: {
              isOpen: true,
              type: 'success',
              text: 'Password has been changed',
            }
          })

          navigate('/login')
        }
      })
      .catch(() => {
        dispatch({
          type: 'SET_SHOW_POPUP',
          payload: {
            isOpen: true,
            type: 'fail',
            text: 'Error accrued while changing password. Try again later.',
          }
        })
      })
  }

  const backendValidate = (values: any) => {
    const validateObj = {
      email: values.email,
      login: values.login,
      gRecaptchaResponse: values.captcha || '',
      devCaptcha: '',
      language: (lang === 'ru') ? 'ru-RU' : 'en-US'
    }

    return validateRegisterForm(validateObj)
  }

  const handleResult = (result: any, actions?: FormikHelpers<any>) => {
    if ( result.token )
      localStorage.setItem('authToken', result.token)

    if ( result.captcha ) {
      dispatch({type: 'SET_SHOW_CAPTCHA', payload: true})
      localStorage.setItem('showCaptcha', 'true')
      actions?.setSubmitting(false)
    }

    if ( result.error ) {
      Object.keys(result.error).forEach((key) => {
        actions?.setFieldError(key, result.error[key])
      })

      return
    }
  }

  return {submitLogin, submitRegister, submitRequest, submitReset, backendValidate, handleResult}
}

export default useFormActions
